// src/App.jsx - Complete updated version with staff reports
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Legal Pages
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Public Pages
import About from './pages/public/About';
import Features from './pages/public/Features';
import Contact from './pages/public/Contact';
import Support from './pages/public/Support';

// Dashboard Pages
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CustomerDashboard from './pages/CustomerDashboard';

// Ticket Pages
import TicketList from './pages/tickets/TicketList';
import TicketDetail from './pages/tickets/TicketDetail';
import CreateTicket from './pages/tickets/CreateTicket';

// Chat Pages
import ChatRoom from './pages/chat/ChatRoom';

// Category Pages
import Categories from './pages/categories/Categories';

// User Management Pages
import Users from './pages/users/Users';
import UserProfile from './pages/users/UserProfile';

// Role Management Pages
import Roles from './pages/roles/Roles';

// Settings Pages
import Settings from './pages/settings/Settings';
import EscalationSettings from './pages/settings/EscalationSettings';
import SystemConfiguration from './pages/settings/SystemConfiguration';
import TicketLogs from './pages/settings/TicketLogs';
import StaffReports from './pages/settings/StaffReports';

// Notification Page
import Notifications from './pages/notifications/Notifications';

// Components
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  devtools: {
    enabled: false,
  },
});

// Chat Route Wrapper with proper user authentication checks
const ChatRouteWrapper = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <ChatRoom user={user} />;
};

// Ticket Chat Wrapper with parameter validation
const TicketChatWrapper = () => {
  const { user, loading } = useAuth();
  const { id } = useParams();
  
  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!id) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600 mb-4">Invalid ticket ID</div>
        <Navigate to="/tickets" replace />
      </div>
    );
  }
  
  return <ChatRoom ticketId={parseInt(id)} user={user} />;
};

// Enhanced permission-based route wrapper
const PermissionRoute = ({ children, permissions, roles, fallback = null }) => {
  const { user, hasPermission, hasAnyPermission, hasRole, hasAnyRole, isAdmin } = useAuth();

  // Admin always has access
  if (isAdmin()) {
    return children;
  }

  // STAFF BYPASS: Check staff status directly to avoid circular dependency
  const isStaffUser = user?.is_agent === true || 
                     user?.is_agent === 1 || 
                     user?.staff_level > 0 ||
                     (user?.roles && Array.isArray(user.roles) && user.roles.some(role => {
                       const roleName = typeof role === 'string' ? role : role.name;
                       return roleName && (
                         roleName.toLowerCase().includes('staff') ||
                         roleName.toLowerCase().includes('agent')
                       );
                     }));

  // If it's a staff user trying to access categories, allow it
  if (isStaffUser && permissions) {
    const permsArray = Array.isArray(permissions) ? permissions : [permissions];
    const isCategoryAccess = permsArray.some(perm => perm && perm.startsWith('categories.'));
    
    if (isCategoryAccess) {
      console.log('Staff bypass: Allowing category access for staff user');
      return children;
    }
  }

  // Check permissions if provided
  if (permissions) {
    const permsArray = Array.isArray(permissions) ? permissions : [permissions];
    if (hasAnyPermission && hasAnyPermission(permsArray)) {
      return children;
    }
  }

  // Check roles if provided
  if (roles) {
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    if (hasAnyRole && hasAnyRole(rolesArray)) {
      return children;
    }
  }

  // Access denied - return fallback or error message
  return fallback || (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
        <p className="text-xl text-gray-600 mb-6">
          You don't have permission to access this page.
        </p>
        <div className="space-y-2 text-sm text-gray-500">
          {permissions && (
            <p>Required permissions: {Array.isArray(permissions) ? permissions.join(', ') : permissions}</p>
          )}
          {roles && (
            <p>Required roles: {Array.isArray(roles) ? roles.join(', ') : roles}</p>
          )}
          <p>User type: {isStaffUser ? 'Staff' : 'Customer'}</p>
          <p>User ID: {user?.id}</p>
        </div>
        <a 
          href="/dashboard" 
          className="mt-6 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
};

// Smart dashboard component that routes based on user role and permissions
const DashboardRouter = () => {
  const { isAdmin, isAgent, isCustomerCare, canManageTickets, user, getStaffLevel } = useAuth();
  
  console.log('DashboardRouter - Role check:', {
    isAdmin: isAdmin(),
    isAgent: isAgent(),
    isCustomerCare: isCustomerCare(),
    canManageTickets: canManageTickets(),
    staffLevel: getStaffLevel(),
    userType: user?.user_type,
    roles: user?.roles
  });
  
  // Admin and high-level managers get admin dashboard
  if (isAdmin() || isCustomerCare() || canManageTickets()) {
    return <AdminDashboard />;
  }
  
  // Staff/agents get staff dashboard
  if (isAgent() && getStaffLevel() > 0) {
    return <StaffDashboard />;
  }
  
  // Regular customers get customer dashboard
  return <CustomerDashboard />;
};

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    style: {
                      background: '#10b981',
                    },
                  },
                  error: {
                    style: {
                      background: '#ef4444',
                    },
                  },
                }}
              />
              
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<Navigate to="/login" replace />} />
                  <Route path="login" element={<Login />} />
                  <Route path="register" element={<Register />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  
                  {/* Public Pages */}
                  <Route path="about" element={<About />} />
                  <Route path="features" element={<Features />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="support" element={<Support />} />

                  {/* Legal Pages */}
                  <Route path="terms-of-service" element={<TermsOfService />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<TermsOfService />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                </Route>

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <PrivateLayout />
                  </ProtectedRoute>
                }>
                  {/* Dashboard with role-based routing */}
                  <Route path="dashboard" element={<DashboardRouter />} />
                  
                  {/* Tickets - Available to all authenticated users */}
                  <Route path="tickets">
                    <Route index element={<TicketList />} />
                    <Route path="new" element={<CreateTicket />} />
                    <Route path=":id" element={<TicketDetail />} />
                    {/* Ticket chat route with proper user handling */}
                    <Route path=":id/chat" element={<TicketChatWrapper />} />
                  </Route>
                  
                  {/* Categories */}
                  <Route path="categories" element={
                    <PermissionRoute permissions={['categories.view']}>
                      <Categories />
                    </PermissionRoute>
                  } />
                  
                  {/* User Management - Admin and those with user management permissions */}
                  <Route path="users" element={
                    <PermissionRoute permissions={['users.view', 'manage-users']}>
                      <Users />
                    </PermissionRoute>
                  } />
                  
                  {/* User Profile - Available to all users */}
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="users/:id" element={
                    <PermissionRoute permissions={['users.view', 'manage-users']}>
                      <UserProfile />
                    </PermissionRoute>
                  } />
                  
                  {/* Role Management - Admin and system managers */}
                  <Route path="roles" element={
                    <PermissionRoute permissions={['roles.view', 'manage-system']}>
                      <Roles />
                    </PermissionRoute>
                  } />
                  
                  {/* Settings */}
                  <Route path="settings">
                    <Route index element={<Settings />} />
                    <Route path="escalation" element={
                      <PermissionRoute permissions={['escalation.view', 'escalation.update']}>
                        <EscalationSettings />
                      </PermissionRoute>
                    } />
                    <Route path="system" element={
                      <PermissionRoute permissions={['config.view', 'manage-system']}>
                        <SystemConfiguration />
                      </PermissionRoute>
                    } />
                    <Route path="logs" element={
                      <PermissionRoute permissions={['logs.view', 'manage-system']}>
                        <TicketLogs />
                      </PermissionRoute>
                    } />
                    <Route path="staff-reports" element={
                      <PermissionRoute permissions={['reports.view']}>
                        <StaffReports />
                      </PermissionRoute>
                    } />
                  </Route>
                  
                  {/* Notifications - Available to all authenticated users */}
                  <Route path="notifications" element={<Notifications />} />
                  
                  {/* Standalone Chat route with proper authentication */}
                  <Route path="chat" element={<ChatRouteWrapper />} />
                  
                  {/* Redirect root to dashboard */}
                  <Route index element={<Navigate to="/dashboard" replace />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-gray-900">404</h1>
                      <p className="mt-4 text-xl text-gray-600">Page not found</p>
                      <a href="/dashboard" className="mt-6 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Go to Dashboard
                      </a>
                    </div>
                  </div>
                } />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;